import { useEffect, useState } from 'react';
import { AlertCircle, Check, Save } from 'lucide-react';
import { irancharsouqAdminApi } from '../../api/irancharsouqAdminApi';
import ChangePasswordForm from '@/components/ChangePasswordForm';
import LocationPicker from '@/features/locations/components/LocationPicker';
import PickupLocationSection from '@/features/locations/components/PickupLocationSection';
import { persianDigits } from '@/features/reports/lib/format';

const EMPTY_PROFILE = {
  first_name: '',
  last_name: '',
  email: '',
  address: '',
  postal_code: '',
  location_id: null,
  latitude: null,
  longitude: null,
  pickup_location_id: null,
  pickup_latitude: null,
  pickup_longitude: null,
  pickup_address: '',
  vendor_local_phone: '',
  vendor_shop_name_fa: '',
  vendor_shop_name_en: '',
  shaba_number: '',
  commission_rate: '',
};

export default function AdminProfile() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(EMPTY_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    irancharsouqAdminApi.getProfile()
      .then((data) => {
        setProfile(data);
        setForm({
          ...EMPTY_PROFILE,
          ...data,
          commission_rate: data.commission_rate ?? '',
        });
      })
      .catch((requestError) => {
        console.error(requestError);
        setError('خطا در دریافت پروفایل');
      })
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setSuccess('');
  };

  const handleLocationPatch = (patch) => {
    setForm((current) => ({ ...current, ...patch }));
    setSuccess('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    const payload = { ...form };
    if (payload.commission_rate !== '') payload.commission_rate = Number(payload.commission_rate);

    try {
      const updated = await irancharsouqAdminApi.updateProfile(payload);
      setProfile(updated);
      setForm((current) => ({ ...current, ...updated }));
      setSuccess('اطلاعات پروفایل با موفقیت ذخیره شد.');
    } catch (requestError) {
      console.error(requestError);
      setError('ذخیره پروفایل با خطا مواجه شد.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="dashboard-loading">در حال دریافت اطلاعات...</div>;
  if (!profile && error) return <div className="admin-table-error"><AlertCircle size={16} />{error}</div>;

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">پروفایل</h1>
          <p className="admin-page-subtitle">ویرایش اطلاعات حساب ادمین ایران چارسوق</p>
        </div>
      </div>

      {error && <div className="admin-table-error"><AlertCircle size={16} />{error}</div>}
      {success && <div className="admin-profile-success"><Check size={16} />{success}</div>}

      <form className="card admin-profile-form" onSubmit={handleSubmit}>
        <div className="admin-profile-section">
          <h2>اطلاعات حساب</h2>
          <div className="admin-profile-form-grid">
            <label>نام<input name="first_name" value={form.first_name} onChange={handleChange} required /></label>
            <label>نام خانوادگی<input name="last_name" value={form.last_name} onChange={handleChange} required /></label>
            <label>ایمیل<input name="email" type="email" value={form.email || ''} onChange={handleChange} /></label>
            <label>شماره موبایل<input value={persianDigits(profile?.mobile_phone)} disabled /></label>
            <label>کد ملی<input value={persianDigits(profile?.national_id)} disabled /></label>
            <label>کد پستی<input name="postal_code" value={form.postal_code || ''} onChange={handleChange} /></label>
          </div>
          <label className="admin-profile-wide-field">آدرس<textarea name="address" value={form.address || ''} onChange={handleChange} /></label>

          <LocationPicker
            value={{
              location_id: form.location_id,
              latitude: form.latitude,
              longitude: form.longitude,
              address: form.address,
            }}
            onChange={handleLocationPatch}
          />
        </div>

        <PickupLocationSection form={form} setForm={setForm} />

        <div className="admin-profile-section">
          <h2>اطلاعات شعبه</h2>
          <div className="admin-profile-form-grid">
            <label>شماره موبایل شعبه<input name="vendor_local_phone" value={form.vendor_local_phone || ''} onChange={handleChange} /></label>
            <label>نام شعبه فارسی<input name="vendor_shop_name_fa" value={form.vendor_shop_name_fa || ''} onChange={handleChange} /></label>
            <label>نام لاتین شعبه برای جست‌وجو<input name="vendor_shop_name_en" value={form.vendor_shop_name_en || ''} onChange={handleChange} /></label>
            <label>شماره شبا<input name="shaba_number" value={form.shaba_number || ''} onChange={handleChange} /></label>
            <label>نرخ کمیسیون<input name="commission_rate" type="number" min="0" max="100" step="0.01" value={form.commission_rate || ''} onChange={handleChange} /></label>
          </div>
        </div>

        <button type="submit" className="admin-refresh-btn" disabled={saving}>
          <Save size={16} />
          <span>{saving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}</span>
        </button>
      </form>
      <ChangePasswordForm />
    </div>
  );
}